import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["fileField", "previewWrapper", "addButton"];
  static values = {
    validTypes: { type: String, default: "image/*" },
    maxFiles: { type: Number, default: 5 },
    existingFiles: { type: Array, default: [] },
    formName: { type: String },
    showRemoveButton: { type: Boolean, default: true }
  };

  initialize() {
    this.validTypesArray = this.validTypesValue.split(',').map(type => type.trim());
    this.selectedFiles = new Map();
    this.fileCounter = 0;
  }

  connect() {
    if (this.hasFileFieldTarget) {
      this.fileFieldTarget.classList.add('custom-file-input');
      this.fileFieldTarget.setAttribute('accept', this.validTypesValue);
      this.fileFieldTarget.addEventListener("change", this.handleFilesSelected.bind(this));

      if (this.hasAddButtonTarget) {
        this.updateAddButtonVisibility();
      }

      if (this.existingFilesValue && this.existingFilesValue.length > 0) {
        this.renderExistingFiles();
      }
    }
  }

  disconnect() {
    if (this.hasFileFieldTarget) {
      this.fileFieldTarget.removeEventListener("change", this.handleFilesSelected.bind(this));
    }
  }

  renderExistingFiles() {
    this.existingFilesValue.forEach((fileData, index) => {
      if (fileData.id === null || fileData.id === undefined) { return; }
      const fileId = `existing-${fileData.id}`;

      this.renderThumbnail(fileData.url, fileData.name || `ERR #${index + 1}`, fileId, true, fileData.id);
      this.fileCounter++;
    });

    this.updateAddButtonVisibility();
  }

  handleFilesSelected() {
    const files = this.fileFieldTarget.files;

    if (!files || files.length === 0) {
      return;
    }

    const currentCount = this.getTotalFilesCount();
    const remainingSlots = this.maxFilesValue - currentCount;

    if (remainingSlots <= 0) {
      return;
    }

    const filesToAdd = Math.min(remainingSlots, files.length);

    if (currentCount === 0) {
      this.previewWrapperTarget.innerHTML = "";
    }

    for (let i = 0; i < filesToAdd; i++) {
      const file = files[i];

      const isAcceptable = this.isAcceptableFileType(file);
      if (!isAcceptable) {
        continue;
      }

      const fileId = `new-${this.fileCounter++}`;
      this.selectedFiles.set(fileId, file);

      const objectUrl = URL.createObjectURL(file);
      this.renderThumbnail(objectUrl, file.name, fileId, false);
    }

    // Clear the file input to allow selecting the same file again
    this.fileFieldTarget.value = '';

    this.updateAddButtonVisibility();
  }

  renderThumbnail(src, filename, fileId, isExisting, existingId) {
    const thumbnailContainer = document.createElement('div');
    thumbnailContainer.className = 'thumbnail-item';
    thumbnailContainer.dataset.fileId = fileId;

    if (this.showRemoveButtonValue) {
      const removeButton = document.createElement('button');
      removeButton.className = 'thumbnail-remove-btn';
      removeButton.type = 'button';
      removeButton.setAttribute('data-action', 'click->fileinput-upload#removeFile');
      removeButton.setAttribute('data-file-id', fileId);

      const removeIcon = document.createElement('span');
      removeIcon.className = 'material-symbols-outlined';
      removeIcon.textContent = 'delete';

      removeButton.appendChild(removeIcon);
      thumbnailContainer.appendChild(removeButton);
    }

    const thumbnailWrapper = document.createElement('div');
    thumbnailWrapper.className = 'thumbnail-wrapper';

    const img = document.createElement('img');
    img.classList.add('thumbnail-image');
    img.src = src;

    thumbnailWrapper.appendChild(img);
    thumbnailContainer.appendChild(thumbnailWrapper);

    const caption = document.createElement('div');
    caption.className = 'thumbnail-caption';
    
    if (!isExisting) {
      caption.classList.add('thumbnail-caption-new');
    }

    const captionText = document.createElement('div');
    captionText.className = 'thumbnail-caption-text';
    captionText.textContent = filename;
    caption.appendChild(captionText);

    thumbnailContainer.appendChild(caption);

    // Check if the caption text overflows and add the 'caption-overflow' class
    setTimeout(() => {
      if (caption.offsetWidth < captionText.scrollWidth) {
        caption.classList.add('caption-overflow');
      }
    }, 0);

    this.previewWrapperTarget.appendChild(thumbnailContainer);

    if (!isExisting) {
      // For new files
      const inputName = this.element.dataset.fileinputUploadInputNameValue;
      const fileIndex = fileId.replace('new-', '');

      // If it's a single or multiple file upload
      const isMultiple = this.fileFieldTarget.multiple;

      // Create a hidden file input for the actual file
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.name = isMultiple ? `${inputName}[]` : inputName;
      fileInput.dataset.fileIndex = fileIndex;
      fileInput.style.display = 'none';
      thumbnailContainer.appendChild(fileInput);

      // Save the file in selectedFiles map
      const file = this.selectedFiles.get(fileId);
      if (file) {
        this.attachFileToForm(file, fileInput);
      }

    } else if (existingId) {
    }

    if (!isExisting) {
      img.onload = () => {
        URL.revokeObjectURL(src);
      };
    }
  }

  removeFile(event) {
    const fileId = event.currentTarget.dataset.fileId;
    const thumbnailElement = this.previewWrapperTarget.querySelector(`.thumbnail-item[data-file-id="${fileId}"]`);

    if (thumbnailElement) {
      if (fileId.startsWith('existing-')) {
        const existingId = fileId.replace('existing-', '');
        const inputName = this.element.dataset.fileinputUploadInputNameValue;

        // Use the simplified parameter format
        const destroyInput = document.createElement('input');
        destroyInput.type = 'hidden';
        destroyInput.name = `${this.formNameValue}[_destroy_attachment_ids][]`;
        destroyInput.value = existingId;
        thumbnailElement.appendChild(destroyInput);
        
        // Hide thumbnail but keep it in the DOM to submit deletion data
        thumbnailElement.classList.add('d-none');
        
      } else {
        // For new files remove completely
        thumbnailElement.remove();
        if (fileId.startsWith('new-')) {
          this.selectedFiles.delete(fileId);
        }
      }

      this.updateAddButtonVisibility();
    }
  }

  updateAddButtonVisibility() {
    if (!this.hasAddButtonTarget) return;

    const totalFiles = this.getTotalFilesCount();

    if (totalFiles >= this.maxFilesValue) {
      this.addButtonTarget.classList.add("d-none");
    } else {
      this.addButtonTarget.classList.remove("d-none");
    }
  }

  getTotalFilesCount() {
    const newFilesCount = this.selectedFiles.size;
    const existingFilesCount = this.previewWrapperTarget.querySelectorAll('.thumbnail-item[data-file-id^="existing-"]:not(.d-none)').length;
    return newFilesCount + existingFilesCount;
  }

  isAcceptableFileType(file) {
    return this.validTypesArray.some(type => {
      if (type.endsWith('/*')) {
        const mainType = type.split('/')[0];
        return file.type.startsWith(`${mainType}/`);
      }
      return file.type === type;
    });
  }

  attachFileToForm(file, hiddenInput) {
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    hiddenInput.files = dataTransfer.files;
  }
}
